import Atom from './atom';
import parser from './parser';
import State from './state';
import HotProps from './hotprops';
import { apiAddon, proxyState } from '../apiUpgrade/store.parser';

const Stores = {};

class StoreEngine extends Atom {
  static create(namespace) {
    const store = new StoreEngine();
    Stores[namespace] = store;
    return store;
  }

  static getStore(namespace) {
    return namespace ? Stores[namespace] : Stores;
  }

  isRoot = true;

  constructor() {
    super({});

    this.ready = false;
  }

  /**
   * overwrite Atom's get to report variable usage
   * @param {String} key variable name or path
   */
  get(key) {
    const item = super.get(key);
    if (item == null) {
      this.reportObserved(key || '');
    }
    return item;
  }

  /**
   * overwrite Atom's getData to report variable usage
   */
  getData(key, force) {
    if (key == null) {
      this.reportObserved('');
    }
    return super.getValue(key, force);
  }

  setContext(ctx) {
    this.context = ctx;
  }

  getContext() {
    return this.context;
  }

  getRoot() {
    return this;
  }

  getPath() {
    return '<root>';
  }

  getVariables() {
    if (this.lastVariablesStat === this.updateStat) {
      return this.variables;
    }
    const variables = {};
    this.forEach((item, key) => {
      variables[key] = item.toJS();
      variables[`$${key}`] = item;
    });
    this.variables = variables;
    apiAddon(this);
    this.lastVariablesStat = this.updateStat;
    return this.variables;
  }

  isReady() {
    return this.ready;
  }

  prepare(loaders) {
    if (this.ready) return Promise.resolve();
    this.ready = true;

    const enableEmitter = this.disableEmitter();
    const always = () => {
      enableEmitter();
    };
    return new Promise((resolve, reject) => {
      Promise.all(loaders.map((loader) => {
        // loader.onData(variables => this.mergeData(variables));
        loader.onData((variables, changed) => {
          const _enableEmitter = this.disableEmitter();
          changed.forEach((key) => {
            const atom = this.get(key);
            if (atom) {
              atom.clear();
            }
          });
          _enableEmitter();
          this.mergeData(variables, changed);
          // this.setData(variables, false, changed);
        });
        return loader.prepare(this);
      })).then(() => {
        this.ready = true;
        this.emitter.emit('ready');
        always();
        resolve();
      }, () => {
        always();
        resolve();
      }).catch((e) => {
        always();
        reject(e);
      });
    });
  }

  createHotProps(propsTemplate, appendVariables, usedTokens, scope, siblingFieldIds) {
    return new HotProps(this, propsTemplate, appendVariables, usedTokens, scope, siblingFieldIds);
  }

  createState(options) {
    return new State(options);
  }

  /**
   * parse template, record used variable names.
   * @param {string} tpl string template, i.e. '${name}'
   * @param {Object} appendVariables values passed from parent, used in repeater
   * @param {Object} usedTokens used tokens, used variable names will be appended in this object
   * @param {Object} scope current scope, if undefined, use StoreEngine
   * @param {Xprops} xprops the xprops holds the template
   */
  parse(tpl, appendVariables, usedTokens, scope, xprops) {
    let data = this.getVariables();
    let candidateNames;
    let path;
    const that = this;
    if (xprops) {
      candidateNames = xprops.getSiblingFieldIds();
      path = xprops.getPath();
    }
    if (scope) {
      scope.__get = scope.get;
      scope.get = (key) => {
        if (key) {
          that.reportObserved(key, path);
        }
        return scope.__get(key);
      };
      const context = this.getContext() || {};
      const { dp = {} } = context;
      data = {
        ...dp.variables,
        ...(appendVariables || {}),
        ...proxyState(scope.getData()), // current scope data
      };
    } else if (appendVariables) {
      data = { ...data, ...appendVariables };
    }
    this.startBatch(candidateNames, path);
    data = this.getAsObserved(scope, data, xprops);
    const result = parser(tpl, data);
    const tokens = this.endBatch();
    if (scope) {
      scope.get = scope.__get;
      delete scope.__get;
    }
    if (typeof usedTokens === 'object') {
      Object.keys(tokens).forEach((token) => {
        usedTokens[token] = true;
      });
    }
    return result;
  }

  onReady(fn) {
    if (this.ready) {
      setTimeout(fn, 0);
      return () => {};
    }
    this.emitter.once('ready', fn);
    return () => {
      this.emitter.removeListener('ready', fn);
    };
  }

  onStoreChange(fn) {
    return this.onChange(fn);
  }

  getOwnerForm() {
    return null;
  }

  getFormData(formId, forceValid) {
    const form = this.getForm(formId);
    if (form) {
      return form.getData(forceValid);
    }
    console.warn(`${formId} is not exist.`);
    return {};
  }

  getFormFieldData(formId, forceValid) {
    const form = this.getForm(formId);
    if (form) {
      return form.getFieldData(forceValid);
    }
    console.warn(`${formId} is not exist.`);
    return {};
  }

  getFormValue(formId, forceValid) {
    const form = this.getForm(formId);
    if (form) {
      return form.getValue(forceValid);
    }
    console.warn(`${formId} is not exist.`);
    return {};
  }

  // ---------------------------------
  // --- Compatibles for old Store ---
  // ---------------------------------

  /**
   * 设置 Store 中对应对象的值
   * @deprecated
   */
  setValue(fieldId, fieldData) {
    const field = this.get(fieldId);
    if (field) {
      field.set('fieldData', fieldData);
    }
  }

  /**
   * 设置 Store 中对应对象的值为 Store 中其他对象的值
   * @deprecated
   */
  setValueWith(targetFieldId, withFieldId) {
    const sourceField = this.get(withFieldId);
    if (!sourceField) {
      return;
    }
    this.setValue(targetFieldId, sourceField.getValue('fieldData'));
  }

  /**
   * 获取 Store 中对应对象的值
   * @deprecated
   */
  getValueWith(fieldId, key) {
    const fieldData = this.getValue(`${fieldId}/fieldData`);
    if (fieldData && key != null) {
      return fieldData[key];
    }
    return fieldData;
  }

  /**
   * 设置 Store 中对应组件的属性值
   * @deprecated
   */
  setProps(fieldId, propName, value) {
    const field = this.get(fieldId);
    if (!field) return;
    field.set(propName, value);
  }

  /**
   * 设置 Store 中对应组件的属性值为其他组件的属性值
   * @deprecated
   */
  setPropsWith(targetFieldId, propName, sourceFieldId, key) {
    this.setProps(targetFieldId, propName, this.getValue(`${sourceFieldId}/${key}`));
  }

  /**
   * 设置 Store 中对应表单组件的显示状态
   * @deprecated  use state.set('behavior', behaviorState)
   */
  setBehavior(fieldId, behaviorState) {
    this.setProps(fieldId, 'behavior', { fieldBehavior: behaviorState });
  }

  /**
   * 设置 Store 中对应 FormId 下所有表单组件的行为值
   * @deprecated
   */
  setBehaviorByGroupId(formId, behaviorState) {
    this.getFieldIdsByGroupId(formId).forEach((key) => {
      this.setBehavior(key, behaviorState);
    });
  }

  /**
   * 获取对应 FormId 下的所有表单组件的 fieldId
   * @deprecated
   */
  getFieldIdsByGroupId(formId) {
    return this.getForm(formId).getItems().map(item => item.getValue('fieldId'));
  }

  /**
   * @deprecated
   */
  update() {
    this.emitter.emit('change');
  }

  /**
   * @deprecated
   */
  preventDefault() {
  }

  // 级连联动时使用，开始级连联动，清空已经抛出的变化
  captureCascade() {
    this.cascadeKeys = [];
  }

  // 级连联动时使用，在级连联动中，把变化记下来，用于下次联动
  cascadeChange(key) {
    if (key && this.cascadeKeys) {
      this.cascadeKeys.push(key);
    }
  }

  // 级连联动时使用，在一个联动迭代结束时，根据清空结束级连联动，或者抛出下次级连联动
  releaseCascade(cascadeLevel) {
    if (cascadeLevel > 0 && this.cascadeKeys.length) {
      // 抛出下一次联动
      this.emitChange(this.cascadeKeys, cascadeLevel - 1);
    } else {
      // 已经迭代够了级连，或者已经没有变化
      this.emitter.emit('cascadeDone', this.cascadeKeys);
    }
  }

  onCascadeDone(fn) {
    this.emitter.once('cascadeDone', fn);
  }

  // used in template computation, remember which variables are used
  startBatch(candidateNames, path) {
    this.inBatch = 1;
    this.variableUsedInBatch = {};
    this.batch = {
      candidateNames,
      path,
    };
  }

  getBathInfo() {
    return this.batch;
  }

  reportObserved(name, path) {
    if (this.inBatch) {
      this.variableUsedInBatch[`${path || ''}${name}`] = true;
    }
  }

  endBatch() {
    this.inBatch = 0;
    this.bath = undefined;
    const used = this.variableUsedInBatch;
    this.variableUsedInBatch = {};
    return used;
  }

  isInBatch() {
    return !!this.inBatch;
  }

  /**
   * when template uses variables, record the usage.
   * Need to observe the changes of these variables and refresh template
   * @param {Atom | null} scope current scope, if the component is in the top, it's null
   * @param {Object} data passed data to template
   * @param {Object} xprops xprops which we pointed
   */
  getAsObserved(scope, data, xprops) {
    let candidateNames = {};
    let path = '';
    if (xprops) {
      candidateNames = xprops.getSiblingFieldIds();
      path = xprops.getPath();
    }
    if (!scope) {
      scope = this;
    }
    const that = this;
    const result = {};
    Object.keys(data).forEach((key) => {
      Object.defineProperty(result, key, {
        get() {
          const entry = scope.get(key);
          if (entry) {
            let keyPath;
            if (typeof entry.getPath === 'function') {
              keyPath = entry.getPath();
            }
            that.reportObserved(key, keyPath);
          } else if (key in that.data.maps) {
            that.reportObserved(key);
          }
          return data[key];
        },
        enumerable: true,
      });
    });
    // for names might use
    Object.keys(candidateNames || {}).forEach((key) => {
      if (!(key in result)) {
        Object.defineProperty(result, key, {
          get() {
            that.reportObserved(key, path);
            return {
              __isMocked__: true,
            };
          },
          enumerable: true,
        });
      }
    });
    // append actions
    let action = this.context && this.context.getAction();
    action = action && action.actionsMap;
    Object.keys(action || {}).forEach((key) => {
      if (!(key in result)) {
        result[key] = (...args) => this.context.execActionWithXProps(key, xprops, ...args);
      }
    });
    // system actions
    if (this.context) {
      this.context.appendSysActions(result);
    }
    return result;
  }
}

if (!window.StoreEngine) {
  window.StoreEngine = StoreEngine;
}

export default window.StoreEngine;
